import { FC } from 'react';
import { CheckCircle2, Clock, XCircle, Ban } from 'lucide-react';

import {
  useGetCourseHearingsQuery,
  useGetCourseHearingQuery,
  type HearingSubmissionSummary,
} from '@teachur/http-client';

import { useSingleCourse } from '../../../../../contexts/SingleCourseProvider';
import { HearingReviewItem } from './HearingReviewItem';
import { SubmissionList } from '../../SubmissionList/SubmissionList';

interface HearingReviewSubmission {
  submission: HearingSubmissionSummary;
  isCancelled: boolean;
}

const getStatusBadge = (submission: HearingSubmissionSummary, isCancelled: boolean) => {
  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FEE]">
        <Ban size={16} className="text-[#E53E3E]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#E53E3E]">Cancelled</span>
      </div>
    );
  }

  if (submission.status === 'approved') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#E6F7EF]">
        <CheckCircle2 size={16} className="text-[#47B881]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#47B881]">Approved</span>
      </div>
    );
  }

  if (submission.status === 'notYet') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FEE]">
        <XCircle size={16} className="text-[#E53E3E]" />
        <span className="[font-size:var(--fs-l5)]! font-semibold text-[#E53E3E]">Not Yet</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FFF9EE]">
      <Clock size={16} className="text-[#FE9B0E]" />
      <span className="[font-size:var(--fs-l5)]! font-semibold text-[#FE9B0E]">Pending Review</span>
    </div>
  );
};

export const CourseHearingSubmissions: FC = () => {
  const { course, activeStep } = useSingleCourse();

  const enabled = activeStep === 'hearing' && !!course?.id;

  const { data: hearingsResponse } = useGetCourseHearingsQuery(
    { courseId: course?.id },
    { enabled }
  );

  const hearingId = hearingsResponse?.data?.[0]?.id;

  const { data: hearing } = useGetCourseHearingQuery(hearingId, {
    enabled: enabled && !!hearingId,
  });

  const attemptSubmissions: HearingReviewSubmission[] = (hearing?.attempts ?? [])
    .filter((attempt) => !!attempt.hearingSubmission)
    .filter((attempt) => {
      const isCancelled = attempt.status === 'rejected';
      const isOpen =
        attempt.hearingSubmission!.status === 'pending' ||
        attempt.hearingSubmission!.status === 'inReview';
      return isCancelled || !isOpen;
    })
    .map((attempt) => ({
      submission: attempt.hearingSubmission!,
      isCancelled: attempt.status === 'rejected',
    }));

  const previousReviews: HearingReviewSubmission[] = (hearing?.previousReviews ?? []).map(
    (submission) => ({
      submission,
      isCancelled: false,
    })
  );

  const submissions = [...attemptSubmissions, ...previousReviews].sort(
    (a, b) =>
      new Date(b.submission.createdAt).getTime() - new Date(a.submission.createdAt).getTime()
  );

  if (!submissions.length) return null;

  return (
    <SubmissionList title="Your Hearing Reviews">
      {submissions.map(({ submission, isCancelled }) => (
        <HearingReviewItem
          key={submission.id}
          submission={submission}
          isCancelled={isCancelled}
          statusBadge={getStatusBadge(submission, isCancelled)}
        />
      ))}
    </SubmissionList>
  );
};
